const isFutureYear = (year) => {
  return year > new Date().getFullYear();
};

const isValidUSZip = (zip) => {
  let regex = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
  return regex.test(zip);
};

const isValidFieldResponse = async (response, field) => {
  if (!response) return false;
  switch (field.type) {
    case "address": {
      // Check valid zip code
      if (
        response.country === 1 &&
        response.zip &&
        !isValidUSZip(response.zip)
      ) {
        return false;
      }
      // Check address is complete
      if (
        !response.city ||
        !response.state ||
        !response.addr1 ||
        !response.zip
        /*
        Note: We have purposefully removed validating the country here. Some networks have users with addresses on file with no country associated
        (appears to maybe be related to an separate, old bug). If that network uses an address field where the user is not allowed to change their 
        country, but they have no country for the current address, they get into a state where they can't submit.
        */
      ) {
        return false;
        // errorMessages.addressIncomplete;
      }
      return true;
    }
    case "date": {
      // Check date is complete
      if (!response.month || !response.day) {
        return false;
        // errorMessages.dateIncomplete;
      }
      // Check if year is in future and relevant
      if (
        field.excludeFutureYear &&
        response?.year &&
        isFutureYear(response.year)
      ) {
        return false;
        // errorMessages.yearIsFuture;
      }
      // Check if the year is within an acceptable range
      if (field.maxPastYears) {
        let today = new Date();
        let date = new Date(
          `${response.year}-${response.month}-${response.day}`
        );
        if (
          response?.year &&
          today.getFullYear() - date.getFullYear() > field.maxPastYears
        ) {
          return false;
          //errorMessages.yearIsTooFar;
        }
      }
      return true;
    }
    case "picker": {
      if (response.length <= 0) return false;
      return true;
    }
    case "text": {
      if (!response) return false;
      return true;
    }
    case "number": {
      if (isNaN(response)) {
        return false;
        //errorMessages.numbersOnly;
      }
      return true;
    }
    case "file": {
      // Files have to be uploaded prior to subission to get relevant error message before hand.
      // This function uploads the files and populates any errors to CrossFeatureComponents/ExtendedFields/UploadField.js.
      // This function is also defined there and passed up the tree.
      if (typeof response === "function") {
        return (await response())?.success || false; // Calling this function will populate any error messages using the useHandleUploadFile hook.
      }
      // If it is not a function, that means the user is not trying to upload a file
      return true;
    }
    default:
      return null;
  }
};

const getRespondentRequiredFieldErrors = async (
  respondent,
  requiredUserFields,
  extendedFieldInputResponses
) => {
  const errors = [];
  if (!requiredUserFields?.length) return errors;
  const respondentInputResponses =
    extendedFieldInputResponses[respondent.userID] || {};
  for (const field of requiredUserFields) {
    // Check to see if there is a required response at all
    if (!(field.fieldID in respondentInputResponses)) {
      errors.push(field.fieldID);
    }
    // If there is, validate it
    else {
      // file types slip through to here. We need to validate the responses here because this is where the file actually gets uploaded.
      // So if there is no file, and it's not required, we skip validation. But if there is value, we still validate.
      if (!field.inputIsRequired && !respondentInputResponses[field.fieldID])
        continue;
      const isValid = await isValidFieldResponse(
        respondentInputResponses[field.fieldID],
        field
      );
      if (!isValid) errors.push(field.fieldID);
    }
  }
  return errors;
};

const getSharedRequiredFieldErrors = async (
  requiredSharedFields,
  sharedExtendedFieldInputResponses
) => {
  const errors = [];
  if (!requiredSharedFields?.length) return errors;
  for (const field of requiredSharedFields) {
    // Check to see if there is a required response at all
    if (!(field.fieldID in sharedExtendedFieldInputResponses)) {
      errors.push(field.fieldID);
    }
    // If there is, validate it
    else {
      // file types slip through to here. We need to validate the responses here because this is where the file actually gets uploaded.
      // So if there is no file, and it's not required, we skip validation. But if there is value, we still validate.
      if (
        !field.inputIsRequired &&
        !sharedExtendedFieldInputResponses[field.fieldID]
      )
        continue;
    }
    const isValid = await isValidFieldResponse(
      sharedExtendedFieldInputResponses[field.fieldID],
      field
    );
    if (!isValid) errors.push(field.fieldID);
  }
  return errors;
};

const getExtendedFieldResponsesValidationErrors = async (
  respondents,
  extendedFields = [],
  extendedFieldInputResponses,
  checkAllRespondents = false
) => {
  const selectedRespondents = checkAllRespondents
    ? respondents
    : respondents.filter((r) => r.isChecked);

  const requiredUserFields = extendedFields.filter(
    ({ inputIsRequired, entityType, type }) => {
      if (entityType !== "user") return false; // We'll check this in getSharedExtendedFieldResponsesValidationErrors.
      if (type === "file") {
        // Why do we include non-required file types value here?
        // because file types need to upload on sumit, and should block submission on failure. This was a good place to do that.
        // If the field does not have value but is not required, then it is ignored in getRespondentRequiredFieldErrors.
        return true;
      }
      return inputIsRequired;
    }
  );
  const errors = {};
  for (const respondent of selectedRespondents) {
    const respondentErrors = await getRespondentRequiredFieldErrors(
      respondent,
      requiredUserFields,
      extendedFieldInputResponses
    );
    if (respondentErrors?.length) errors[respondent.userID] = respondentErrors;
  }
  if (Object.keys(errors).length) return errors;
  return null;
};

const getSharedExtendedFieldResponsesValidationErrors = async (
  extendedFields = [],
  sharedExtendedFieldInputResponses
) => {
  const requiredSharedFields = extendedFields.filter(
    ({ inputIsRequired, entityType, type }) => {
      if (entityType === "user") return false; // We'll check this in getExtendedFieldResponsesValidationErrors
      if (type === "file") {
        // Why do we include non-required file types value here?
        // because file types need to upload on sumit, and should block submission on failure. This was a good place to do that.
        // If the field does not have value but is not required, then it is ignored in getRespondentRequiredFieldErrors.
        return true;
      }
      return inputIsRequired;
    }
  );
  const sharedRequiredFieldErrors = await getSharedRequiredFieldErrors(
    requiredSharedFields,
    sharedExtendedFieldInputResponses
  );
  if (Object.keys(sharedRequiredFieldErrors).length)
    return sharedRequiredFieldErrors;
  return null;
};

export {
  getExtendedFieldResponsesValidationErrors,
  getSharedExtendedFieldResponsesValidationErrors,
};
